<script>
/**
 * @component LabCatalog
 * @description ABM de laboratorios: determinaciones y rutinas.
 * Tab 1: lista/CRUD de determinaciones individuales.
 * Tab 2: lista/CRUD de rutinas (combo de determinaciones).
 * @example <LabCatalog />
 */
import labCatalogService from "@/services/lab-catalog";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 300;

export default {
  name: "LabCatalog",
  components: { Spinner },

  data() {
    return {
      /** @description Tab activo: 'labs' o 'routines' */
      activeTab: "labs",

      // ── Determinaciones ──
      isLoadingLabs: false,
      searchLabs: "",
      labs: [],

      isLabFormVisible: false,
      isLabFormLoading: false,
      labFormMode: "create",
      labForm: this.emptyLabForm(),

      // ── Rutinas ──
      isLoadingRoutines: false,
      searchRoutines: "",
      routines: [],
      allLabs: [], // para el selector de determinaciones en rutina

      isRoutineFormVisible: false,
      isRoutineFormLoading: false,
      routineFormMode: "create",
      routineForm: this.emptyRoutineForm(),
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /** @returns {string} Título del dialog de determinación */
    labFormTitle() {
      return this.labFormMode === "create"
        ? "Nueva determinación"
        : "Editar determinación";
    },

    /** @returns {string} Título del dialog de rutina */
    routineFormTitle() {
      return this.routineFormMode === "create"
        ? "Nueva rutina"
        : "Editar rutina";
    },
  },

  created() {
    this.fetchLabs();
    this.fetchRoutines();
  },

  methods: {
    /** @returns {Object} Formulario vacío de determinación */
    emptyLabForm() {
      return { code: "", description: "" };
    },

    /** @returns {Object} Formulario vacío de rutina */
    emptyRoutineForm() {
      return { name: "", code: "", description: "", labs: [] };
    },

    // ── Determinaciones ──

    /**
     * @description Carga determinaciones con filtro de búsqueda
     */
    async fetchLabs() {
      this.isLoadingLabs = true;
      try {
        const params = {};
        if (this.searchLabs) params.search = this.searchLabs;
        this.labs = await labCatalogService.getLabs(params);
        // Cache para el selector de rutinas
        if (!this.searchLabs) this.allLabs = this.labs;
      } catch (error) {
        this.$message.error("Error al cargar determinaciones");
        console.error(error);
      } finally {
        this.isLoadingLabs = false;
      }
    },

    debounceSearchLabs: debounce(function () {
      this.fetchLabs();
    }, DEBOUNCE_MS),

    openCreateLab() {
      this.labFormMode = "create";
      this.labForm = this.emptyLabForm();
      this.isLabFormVisible = true;
    },

    openEditLab(lab) {
      this.labFormMode = "edit";
      this.labForm = {
        _id: lab._id,
        code: lab.code || "",
        description: lab.description || "",
      };
      this.isLabFormVisible = true;
    },

    /**
     * @description Guarda una determinación (crear o editar)
     */
    async saveLabForm() {
      if (!this.labForm.code || !this.labForm.description) {
        this.$message.warning("Código y descripción son requeridos");
        return;
      }
      this.isLabFormLoading = true;
      try {
        if (this.labFormMode === "create") {
          await labCatalogService.createLab(this.labForm);
          this.$message.success("Determinación creada");
        } else {
          await labCatalogService.updateLab(this.labForm._id, this.labForm);
          this.$message.success("Determinación actualizada");
        }
        this.isLabFormVisible = false;
        this.fetchLabs();
      } catch (error) {
        const msg = error.response?.data?.error || "Error al guardar";
        this.$message.error(msg);
      } finally {
        this.isLabFormLoading = false;
      }
    },

    /**
     * @description Elimina una determinación previa confirmación
     * @param {Object} lab
     */
    async deleteLab(lab) {
      try {
        await this.$confirm(
          `¿Eliminar la determinación "${lab.description}" (${lab.code})?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await labCatalogService.deleteLab(lab._id);
        this.$message.success("Determinación eliminada");
        this.fetchLabs();
      } catch {
        // Cancelado
      }
    },

    // ── Rutinas ──

    /**
     * @description Carga rutinas con filtro de búsqueda
     */
    async fetchRoutines() {
      this.isLoadingRoutines = true;
      try {
        const params = {};
        if (this.searchRoutines) params.search = this.searchRoutines;
        this.routines = await labCatalogService.getRoutines(params);
      } catch (error) {
        this.$message.error("Error al cargar rutinas");
        console.error(error);
      } finally {
        this.isLoadingRoutines = false;
      }
    },

    debounceSearchRoutines: debounce(function () {
      this.fetchRoutines();
    }, DEBOUNCE_MS),

    openCreateRoutine() {
      this.routineFormMode = "create";
      this.routineForm = this.emptyRoutineForm();
      this.isRoutineFormVisible = true;
    },

    openEditRoutine(routine) {
      this.routineFormMode = "edit";
      this.routineForm = {
        _id: routine._id,
        name: routine.name || "",
        code: routine.code || "",
        description: routine.description || "",
        labs: (routine.labs || []).map((l) => (l._id ? l._id : l)),
      };
      this.isRoutineFormVisible = true;
    },

    /**
     * @description Guarda una rutina (crear o editar)
     */
    async saveRoutineForm() {
      if (!this.routineForm.name) {
        this.$message.warning("El nombre es requerido");
        return;
      }
      this.isRoutineFormLoading = true;
      try {
        const payload = {
          name: this.routineForm.name,
          code: this.routineForm.code,
          description: this.routineForm.description,
          labs: this.routineForm.labs,
        };
        if (this.routineFormMode === "create") {
          await labCatalogService.createRoutine(payload);
          this.$message.success("Rutina creada");
        } else {
          await labCatalogService.updateRoutine(this.routineForm._id, payload);
          this.$message.success("Rutina actualizada");
        }
        this.isRoutineFormVisible = false;
        this.fetchRoutines();
      } catch (error) {
        const msg = error.response?.data?.error || "Error al guardar";
        this.$message.error(msg);
      } finally {
        this.isRoutineFormLoading = false;
      }
    },

    /**
     * @description Elimina una rutina previa confirmación
     * @param {Object} routine
     */
    async deleteRoutine(routine) {
      try {
        await this.$confirm(
          `¿Eliminar la rutina "${routine.name}"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await labCatalogService.deleteRoutine(routine._id);
        this.$message.success("Rutina eliminada");
        this.fetchRoutines();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
  section
    header.headline
      .headline__title Laboratorio

    el-tabs(v-model="activeTab")
      //- ── Tab Determinaciones ──
      el-tab-pane(label="Determinaciones" name="labs")
        .tab-actions
          el-input.search__input(
            prefix-icon="el-icon-search"
            v-model="searchLabs"
            @input="debounceSearchLabs"
            placeholder="Buscar por código o descripción..."
            clearable
            size="small"
          )
          .button.button--blue(@click="openCreateLab" v-if="isAdmin")
            micon(name="add_circle_outline").button__icon
            span.button__text + Nueva determinación

        .box.box--with-subnav
          .box__content--stretch
            table
              thead
                tr
                  th Código
                  th Descripción
                  th Acciones
              tbody
                spinner(v-if="isLoadingLabs")
                tr(
                  v-else
                  v-for="lab in labs"
                  :key="lab._id"
                  @click="openEditLab(lab)"
                )
                  td {{ lab.code }}
                  td {{ lab.description }}
                  td.actions
                    .actions-container(@click.stop)
                      button.button.button--action(@click="openEditLab(lab)")
                        micon(name="edit")
                      button.button.button--action(@click="deleteLab(lab)" v-if="isAdmin")
                        micon(name="delete_forever")

      //- ── Tab Rutinas ──
      el-tab-pane(label="Rutinas" name="routines")
        .tab-actions
          el-input.search__input(
            prefix-icon="el-icon-search"
            v-model="searchRoutines"
            @input="debounceSearchRoutines"
            placeholder="Buscar rutina..."
            clearable
            size="small"
          )
          .button.button--blue(@click="openCreateRoutine" v-if="isAdmin")
            micon(name="add_circle_outline").button__icon
            span.button__text + Nueva rutina

        .box.box--with-subnav
          .box__content--stretch
            table
              thead
                tr
                  th Nombre
                  th Código
                  th Determinaciones
                  th Acciones
              tbody
                spinner(v-if="isLoadingRoutines")
                tr(
                  v-else
                  v-for="routine in routines"
                  :key="routine._id"
                  @click="openEditRoutine(routine)"
                )
                  td {{ routine.name }}
                  td {{ routine.code }}
                  td {{ (routine.labs || []).length }} determinaciones
                  td.actions
                    .actions-container(@click.stop)
                      button.button.button--action(@click="openEditRoutine(routine)")
                        micon(name="edit")
                      button.button.button--action(@click="deleteRoutine(routine)" v-if="isAdmin")
                        micon(name="delete_forever")

    //- Dialog determinación
    el-dialog(:title="labFormTitle" :visible.sync="isLabFormVisible" width="500px")
      el-form(label-position="top" size="small")
        el-row(:gutter="16")
          el-col(:span="8")
            el-form-item(label="Código")
              el-input(v-model="labForm.code" placeholder="Ej: 51")
          el-col(:span="16")
            el-form-item(label="Descripción")
              el-input(v-model="labForm.description" placeholder="Nombre de la determinación")
      template(#footer)
        el-button(@click="isLabFormVisible = false") Cancelar
        el-button(type="primary" @click="saveLabForm" :loading="isLabFormLoading") Guardar

    //- Dialog rutina
    el-dialog(:title="routineFormTitle" :visible.sync="isRoutineFormVisible" width="600px")
      el-form(label-position="top" size="small")
        el-row(:gutter="16")
          el-col(:span="16")
            el-form-item(label="Nombre")
              el-input(v-model="routineForm.name" placeholder="Ej: Hemograma Completo")
          el-col(:span="8")
            el-form-item(label="Código")
              el-input(v-model="routineForm.code" placeholder="Ej: HEMOGRAMA")
        el-form-item(label="Descripción")
          el-input(v-model="routineForm.description" placeholder="Descripción opcional" type="textarea" :rows="2")
        el-form-item(label="Determinaciones incluidas")
          el-select(
            v-model="routineForm.labs"
            multiple
            filterable
            placeholder="Buscar y seleccionar determinaciones..."
            style="width: 100%"
          )
            el-option(
              v-for="lab in allLabs"
              :key="lab._id"
              :label="`${lab.code} — ${lab.description}`"
              :value="lab._id"
            )
      template(#footer)
        el-button(@click="isRoutineFormVisible = false") Cancelar
        el-button(type="primary" @click="saveRoutineForm" :loading="isRoutineFormLoading") Guardar
</template>

<style lang="scss" scoped>
.headline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.headline__title {
  font-size: 20px;
  font-weight: 700;
  color: $navy;
}

.tab-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search__input {
  width: 280px;
}

tbody td {
  cursor: pointer;
}
</style>
